import { Formik, FormikProps } from 'formik';
import React, { useMemo, useRef } from 'react';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import Button from '@components/Button';
import { TextField } from '@components/FormFields';
import SectionTitle from '@components/SectionTitle';
import CustomSpinner from '@components/Spinner/CustomSpinner';
import { SubscriptionFormProps } from '@components/SubscriptionForm';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { useUserCreate } from '@hooks';
import { RECAPTCHA_SITE_KEY } from '@src/env';
import { LeadType } from '@utils/backend-api/users';
import { extractQueryParams } from '@utils/queryStringHelper';

import validationSchema from './validationSchema';

interface FormValues {
  email: string;
  name: string;
  userEmail?: string;
}

interface SignupFormProps extends Omit<SubscriptionFormProps, 'buttonText'> {
  formLocation: string;
}

const SignupForm = ({ formLocation }: SignupFormProps) => {
  const {
    user: { email, name },
    setUser,
  } = useSubscribeForm();
  const { mutateAsync: createUser, isLoading } = useUserCreate();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const formRef = useRef<FormikProps<FormValues>>();
  const { article_id: referringArticleId } = extractQueryParams(['article_id']);
  const signupFormRef = useRef<HTMLFormElement>(null);

  // Passed userEmail to the form for email validation initial state
  const initialState = useMemo(
    () => ({
      email: email || '',
      name: name || '',
      userEmail: email,
    }),
    [email, name]
  );

  const handleCreateUser = async ({ email, name }: FormValues) => {
    const refToken = await reCaptchaRef.current.executeAsync();
    const createUserParams = {
      email,
      lead_type: LeadType.SUBSCRIBE_H_PAGE,
      form_location: formLocation,
      page: window.location.pathname,
      name,
      recaptcha_token: refToken,
      referring_article_id: referringArticleId,
    };
    const { stripeCustomerId } = await createUser(createUserParams);
    setUser({ email, name, stripeCustomerId });
    reCaptchaRef.current.reset();
  };

  return (
    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
      <Formik
        enableReinitialize
        initialValues={initialState}
        onSubmit={handleCreateUser}
        validationSchema={validationSchema()}
        innerRef={formRef}
      >
        {({ handleSubmit }) => (
          <Form
            ref={signupFormRef}
            className="account-info mb-48 w-full"
            noValidate
            onSubmit={handleSubmit}
          >
            <SectionTitle title="Step 1" />
            <div className="mb flex flex-col items-start gap-8 sm:flex-row sm:gap-16">
              <TextField
                name="name"
                placeholder="Full Name"
                disabled={!!name}
                wrapperClassName="!mt-0 w-full flex-2"
              />
              <TextField
                name="email"
                placeholder="Email"
                disabled={!!email}
                wrapperClassName="!mt-0 w-full flex-2"
              />
              <Button
                id="signupBtn"
                disabled={isLoading || (!!email && !!name)}
                type="submit"
                className="w-full !rounded-2 !border-pro-red !bg-pro-red !capitalize sm:w-[130px] md:w-[248px]"
              >
                {isLoading ? (
                  <CustomSpinner
                    color="#fff"
                    size={10}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  />
                ) : (
                  <>Create</>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ReCAPTCHA>
  );
};

export default SignupForm;
